import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { RESULTS_GALLERY } from "./assets";

// Easy to edit later — change value / suffix / label
const STATS = [
  { value: 15, suffix: "+", label: "All-Americans Developed" },
  { value: 30, suffix: "+", label: "School Records Produced" },
  { value: 500, suffix: "+", label: "Athletes Trained" },
  { value: 5, suffix: "+", label: "Years Coaching Experience" },
];

function CountUp({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span ref={ref} className="font-display font-black text-6xl md:text-7xl leading-none tracking-tighter">
      {n}
      {suffix}
    </span>
  );
}

export default function ResultsThatMatter({ compact = false }) {
  if (compact) {
    return (
      <section
        id="results-proof"
        data-testid="results-proof-section"
        className="py-16 md:py-20 bg-[#0a0a0a] border-t border-white/10"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-8 text-center">
            The Proof Is In The Results
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {STATS.map((s, i) => (
              <div key={s.label} className="bg-[#0a0a0a] p-8 text-center" data-testid={`stat-b-${i}`}>
                <CountUp value={s.value} suffix={s.suffix} />
                <p className="text-white/50 text-xs uppercase tracking-[0.15em] leading-snug mt-3">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="results"
      data-testid="results-section"
      className="py-24 md:py-36 bg-black border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-16"
        >
          <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-6">
            Proof, Not Promises
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            Results That Matter
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="bg-black p-10 md:p-12"
              data-testid={`stat-${i}`}
            >
              <CountUp value={s.value} suffix={s.suffix} />
              <p className="text-white/50 text-sm uppercase tracking-[0.15em] leading-snug mt-4">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 bg-white text-black p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-6"
          data-testid="stat-banner"
        >
          <div>
            <p className="font-mono-accent text-xs tracking-[0.25em] uppercase text-black/50 mb-3">
              Built From NCAA Coaching Experience
            </p>
            <h3 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl leading-none">
              College · High School · Youth
            </h3>
          </div>
          <p className="max-w-xs text-black/70">
            One system, developed across every level of competition — engineered
            to make any athlete faster.
          </p>
        </motion.div>

        {/* Athlete / performance gallery */}
        <div className="mt-16">
          <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-8">
            The Athletes · The Podiums · The Proof
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {RESULTS_GALLERY.map((g, i) => (
              <motion.figure
                key={g.src}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 4) * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`result-photo-${i}`}
                className="group relative overflow-hidden border border-white/10 bg-[#111] aspect-[3/4]"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className={`w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ${g.pos}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
